import { Component, OnInit, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";

import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { EstimatesAddEditComponent } from "@bitwarden/web-vault/app/gloss/estimates/estimates-add-edit/estimates-add-edit.component";
import { Estimate } from "@bitwarden/web-vault/app/models/data/blobby/estimate.data";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { EstimateService } from "@bitwarden/web-vault/app/services/DataService/estimates/estimate-service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";

@Component({
  selector: "app-estimates",
  templateUrl: "./estimates.component.html",
})
export class EstimatesComponent implements OnInit {
  isEditMode = false;
  displayedColumns: string[] = [
    "name",
    "categories",
    "classifications",
    "account",
    "direction",
    "period",
    "value",
    "edit",
  ];
  private dialogueRef: MatDialogRef<EstimatesAddEditComponent>;
  // Shared with the open dialog, which disables Save and Delete while a delete runs.
  private readonly deleting = signal(false);

  dataSource: MatTableDataSource<Estimate>;
  estimates: Estimate[] = [];

  constructor(
    public dialog: MatDialog,
    private estimateService: EstimateService,
    private globalService: GlobalService,
    private dataRepoService: DataRepositoryService,
    private confirmationDialogService: ConfirmationDialogService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.setTable();
    } catch (e) {
      this.globalService.showErrorMessage("errorOccurred", e);
    }
  }

  async setTable() {
    this.estimates = await this.estimateService.getAllEstimates();
    this.dataSource = new MatTableDataSource<Estimate>(this.estimates);
  }

  async openEstimateForm(estimate: Estimate) {
    this.isEditMode = !!estimate;

    this.dialogueRef = this.dialog.open(EstimatesAddEditComponent, {
      width: "800px",
      disableClose: true,
      data: {
        isEditMode: this.isEditMode,
        estimate,
        actionSucceeded: this.actionSucceeded.bind(this),
        delete: this.deleteEstimate.bind(this),
        deleting: this.deleting.asReadonly(),
      },
    });
  }

  async deleteEstimate(estimate: Estimate) {
    if (this.deleting()) {
      return;
    }
    this.deleting.set(true);
    try {
      const confirmed = await this.confirmationDialogService.confirmFor(
        ConfirmationEnum.deletingEstimate,
      );
      if (!confirmed) {
        return;
      }
      if (await this.dataRepoService.deleteEstimate(estimate)) {
        await this.actionSucceeded({ message: "deletedSuccessfully" });
      } else {
        // Nothing has told the user yet: blobbyService.delete() catches its own errors and
        // returns undefined, so DataRepositoryService.delete() doesn't show one either.
        this.globalService.showErrorMessage("errorOccurred", "notDeleted");
      }
    } catch (e) {
      this.globalService.showErrorMessage("errorOccurred", e);
    } finally {
      this.deleting.set(false);
    }
  }

  async actionSucceeded(eventData: { message: string }) {
    // The change is saved: close first, so a failed refresh can't leave the dialog open on it.
    this.dialogueRef?.close();
    this.globalService.showSuccessMessage("succeeded", eventData.message);
    // Caught here: the form's (actionSucceeded) output drops this promise.
    try {
      await this.setTable();
    } catch (e) {
      this.globalService.showErrorMessage("errorOccurred", e);
    }
  }
}
